import {
  StudyMopCreateDtoCategoryEnum,
  StudyMopCreateDtoDirectionEnum,
  type StudyMopSelectDto,
} from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useCreateMeiaMopEntry } from '@queries/meia.query';
import { type MeiaMopFormValues, meiaMopSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import type { FC } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';

type MeiaMopCreateModalProps = {
  isOpen: boolean;
  missionId: string;
  onClose: () => void;
  onCreated: (mop: StudyMopSelectDto) => void;
};

const CATEGORY_OPTIONS = [
  { label: 'Process reliability', value: StudyMopCreateDtoCategoryEnum.PROCESS_RELIABILITY },
  { label: 'Technical / physical', value: StudyMopCreateDtoCategoryEnum.TECHNICAL_PHYSICAL },
  { label: 'Time / latency', value: StudyMopCreateDtoCategoryEnum.TIME_LATENCY },
];

const DIRECTION_OPTIONS = [
  { label: 'Higher is better', value: StudyMopCreateDtoDirectionEnum.HIGHER_IS_BETTER },
  { label: 'Lower is better', value: StudyMopCreateDtoDirectionEnum.LOWER_IS_BETTER },
];

const buildDefaults = (): MeiaMopFormValues => ({
  category: StudyMopCreateDtoCategoryEnum.TECHNICAL_PHYSICAL,
  description: '',
  direction: StudyMopCreateDtoDirectionEnum.HIGHER_IS_BETTER,
  mopTemplateCode: null,
  name: '',
  objective: '',
  threshold: '',
  unit: '',
});

export const MeiaMopCreateModal: FC<MeiaMopCreateModalProps> = (props) => {
  const { isOpen, missionId, onClose, onCreated } = props;
  const { isPending, mutateAsync: createMop } = useCreateMeiaMopEntry();

  const form = useAppForm({
    defaultValues: buildDefaults(),
    onSubmit: async ({ value }) => {
      const validation = meiaMopSchema.safeParse(value);
      if (!validation.success) {
        errorToast({ description: 'Please complete every required field.' });
        return;
      }
      try {
        const created = await createMop({ ...validation.data, missionId });
        successToast({ description: 'Measure of Performance created.' });
        onCreated(created);
        onClose();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to create MoP.';
        errorToast({ description: message });
      }
    },
    onSubmitInvalid: createFormInvalidHandler<MeiaMopFormValues>(),
    validators: { onSubmit: meiaMopSchema },
  });

  return (
    <Modal
      centered
      footer={{
        rightButton: {
          children: isPending ? 'Saving...' : 'Create MoP',
          disabled: isPending,
          onClick: () => void form.handleSubmit(),
        },
      }}
      header={{ title: 'New Measure of Performance' }}
      isOpen={isOpen}
      onClose={onClose}
      width="640px"
    >
      <form
        {...MEIA_AUTOFILL_IGNORE_PROPS}
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.AppField name="name">
            {(field) => (
              <field.TextField
                {...MEIA_AUTOFILL_IGNORE_PROPS}
                label="Name"
                placeholder="e.g., AESA acquisition range"
                required
              />
            )}
          </form.AppField>
          <form.AppField name="description">
            {(field) => (
              <field.TextAreaField
                label="Description"
                placeholder="What is this measure tracking?"
                required
                rows={2}
              />
            )}
          </form.AppField>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <form.AppField name="objective">
              {(field) => (
                <field.TextField
                  {...MEIA_AUTOFILL_IGNORE_PROPS}
                  label="Objective"
                  placeholder="e.g., ≥ 50 NM"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="threshold">
              {(field) => (
                <field.TextField
                  {...MEIA_AUTOFILL_IGNORE_PROPS}
                  label="Threshold"
                  placeholder="e.g., ≥ 35 NM"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="unit">
              {(field) => (
                <field.TextField
                  {...MEIA_AUTOFILL_IGNORE_PROPS}
                  label="Unit"
                  placeholder="e.g., NM, seconds, %"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="category">
              {(field) => (
                <field.SelectField
                  label="Category"
                  options={CATEGORY_OPTIONS}
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="direction">
              {(field) => (
                <field.SelectField
                  label="Direction"
                  options={DIRECTION_OPTIONS}
                  required
                />
              )}
            </form.AppField>
          </div>
        </form.AppForm>
      </form>
    </Modal>
  );
};
